// src/pages/UsermanProfiles.js
import React, { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  Users,
  FileDownIcon,
} from "lucide-react";
import { Dialog, Transition } from "@headlessui/react";
import Swal from "sweetalert2";
import { useSearchParams } from "react-router-dom";
import Papa from "papaparse";
import { Fragment } from "react";
import UsermanMenu from "../components/UsermanMenu";

const getAuthHeader = () => {
  const token = localStorage.getItem("token");
  return { Authorization: `Bearer ${token}` };
};

const UsermanProfiles = () => {
  const [searchParams] = useSearchParams();
  const usermanId = searchParams.get("userman_id");
  const deviceName = searchParams.get("device_name");

  const [profiles, setProfiles] = useState([]);
  const [filteredProfiles, setFilteredProfiles] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const profilesPerPage = 10;
  const [loading, setLoading] = useState(true);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    validity: "",
    nameForUsers: "",
    startsWhen: "",
    price: "",
    overrideSharedUsers: "",
  });

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    if (!usermanId) return;
    try {
      const res = await api.get(`/userman-profile-get/${usermanId}`);
      const data = res.data.data || [];
      setProfiles(data);
      setFilteredProfiles(data);
    } catch (err) {
      console.error("Failed to fetch profiles", err);
    } finally {
      setLoading(false);
    }
  }, [usermanId]);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  useEffect(() => {
    const filtered = profiles.filter((profile) =>
      profile.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredProfiles(filtered);
    setCurrentPage(1);
  }, [searchQuery, profiles]);

  const exportCSV = () => {
    const csv = Papa.unparse(filteredProfiles);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "userman_profiles.csv");
    link.click();
  };

  const openAddModal = () => {
    setEditingProfile(null);
    setFormData({
      name: "",
      validity: "",
      nameForUsers: "",
      startsWhen: "",
      price: "",
      overrideSharedUsers: "",
    });
    setIsModalOpen(true);
  };

  const handleEdit = (profile) => {
    setEditingProfile(profile);
    setFormData({
      name: profile.name,
      validity: profile.validity,
      nameForUsers: profile["name-for-users"] || profile.name_for_users,
      startsWhen: profile["starts-when"] || profile.starts_when,
      price: profile.price,
      overrideSharedUsers:
        profile["override-shared-users"] || profile.override_shared_users,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (profileName) => {
    setLoading(true);
    if (!window.confirm("Yakin ingin menghapus profile ini?")) return;
    try {
      await api.delete(`/userman-profile-delete`, {
        headers: getAuthHeader(),
        data: {
          device_id: usermanId,
          profileName,
        },
      });
      Swal.fire({
        icon: "success",
        title: "Profile berhasil dihapus",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
      fetchProfiles();
    } catch (err) {
      console.error("Gagal menghapus profile", err);
      Swal.fire({
        icon: "error",
        title: "Gagal menghapus profile",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (editingProfile) {
        await api.put(
          `/userman-profile-update`,
          {
            device_id: usermanId,
            profileName: editingProfile.name,
            updates: {
              validity: formData.validity,
              price: formData.price,
              "starts-when": formData.startsWhen,
              "name-for-users": formData.nameForUsers,
              "override-shared-users": formData.overrideSharedUsers,
            },
          },
          {
            headers: getAuthHeader(),
          }
        );
        Swal.fire({
          icon: "success",
          title: "Profile berhasil diperbarui",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      } else {
        await api.post(
          `/userman-profile-add`,
          {
            device_id: usermanId,
            newProfile: formData,
          },
          {
            headers: getAuthHeader(),
          }
        );
        Swal.fire({
          icon: "success",
          title: "Profile berhasil ditambahkan",
          toast: true,
          position: "top-end",
          showConfirmButton: false,
          timer: 2000,
        });
      }
      setIsModalOpen(false);
      fetchProfiles();
    } catch (err) {
      console.error("Gagal menyimpan profile", err);
      Swal.fire({
        icon: "error",
        title: "Gagal menyimpan profile",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 2000,
      });
    } finally {
      setLoading(false);
    }
  };

  const indexOfLastProfile = currentPage * profilesPerPage;
  const indexOfFirstProfile = indexOfLastProfile - profilesPerPage;
  const currentProfiles = filteredProfiles.slice(
    indexOfFirstProfile,
    indexOfLastProfile
  );
  const totalPages = Math.ceil(filteredProfiles.length / profilesPerPage);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="w-16 h-16 border-4 border-blue-500 border-dashed rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Users /> {deviceName} /
          <UsermanMenu userman_id={usermanId} device_name={deviceName} />/
          Profiles
        </h1>
        <button
          onClick={exportCSV}
          className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-md"
        >
          <FileDownIcon size={18} /> Export CSV
        </button>
      </div>

      <div className="flex gap-2 mb-4">
        <input
          type="text"
          placeholder="Cari Nama Profil..."
          className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400 flex-1"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <button
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl flex items-center gap-2 shadow-md"
          onClick={openAddModal}
        >
          <PlusIcon size={18} /> Tambah Profile
        </button>
      </div>

      {/* Modal with Headless UI */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-10"
          onClose={() => setIsModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-600 p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-black dark:text-white"
                  >
                    {editingProfile ? "Edit Profile" : "Tambah Profile"}
                  </Dialog.Title>
                  <br />
                  <div className="mt-2 flex flex-col gap-3">
                    <input
                      className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                      type="text"
                      placeholder="Name"
                      value={formData.name || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                    />
                    <select
                      className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                      value={formData.validity || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, validity: e.target.value })
                      }
                    >
                      <option className="text-gray-400" value="">
                        Validity
                      </option>
                      <option className="text-gray-800" value="unlimited">
                        unlimited
                      </option>
                      <option className="text-gray-800" value="1h">
                        1 jam
                      </option>
                      <option className="text-gray-800" value="3h">
                        3 jam
                      </option>
                      <option className="text-gray-800" value="5h">
                        5 jam
                      </option>
                      <option className="text-gray-800" value="10h">
                        10 jam
                      </option>
                      <option className="text-gray-800" value="1d">
                        1 hari
                      </option>
                      <option className="text-gray-800" value="2d">
                        2 hari
                      </option>
                      <option className="text-gray-800" value="7d">
                        1 minggu
                      </option>
                    </select>
                    <input
                      className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                      type="number"
                      placeholder="Harga"
                      value={formData.price || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, price: e.target.value })
                      }
                    />
                    <input
                      className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                      type="text"
                      placeholder="Nama untuk User"
                      value={formData.nameForUsers || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          nameForUsers: e.target.value,
                        })
                      }
                    />
                    <select
                      className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                      value={formData.startsWhen || ""}
                      onChange={(e) =>
                        setFormData({ ...formData, startsWhen: e.target.value })
                      }
                    >
                      <option className="text-gray-400" value="">
                        Mulai Saat
                      </option>
                      <option className="text-gray-800" value="assigned">
                        assigned
                      </option>
                      <option className="text-gray-800" value="first auth">
                        first auth
                      </option>
                    </select>
                    <select
                      className="w-full border text-black dark:text-white border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-700 px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-400"
                      value={formData.overrideSharedUsers || ""}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          overrideSharedUsers: e.target.value,
                        })
                      }
                    >
                      <option className="text-gray-400" value="">
                        Shared Users
                      </option>
                      <option className="text-gray-800" value="unlimited">
                        unlimited
                      </option>
                      <option className="text-gray-800" value="off">
                        off
                      </option>
                    </select>
                  </div>

                  <div className="mt-4 flex justify-end gap-2">
                    <button
                      className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                      onClick={() => setIsModalOpen(false)}
                    >
                      Batal
                    </button>
                    <button
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                      onClick={handleSubmit}
                    >
                      Simpan
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      <div className="overflow-x-auto rounded-xl shadow bg-white dark:bg-gray-800">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th className="p-3 text-left font-semibold">Nama</th>
              <th className="p-3 text-left font-semibold">Harga</th>
              <th className="p-3 text-left font-semibold">Validitas</th>
              <th className="p-3 text-left font-semibold">Starts When</th>
              <th className="p-3 text-left font-semibold">Shared Users</th>
              <th className="p-3 text-left font-semibold">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {currentProfiles.map((profile) => (
              <tr key={profile.id} className="border-t dark:border-gray-700">
                <td className="p-3">{profile.name}</td>
                <td className="p-3">{profile.price}</td>
                <td className="p-3">{profile.validity}</td>
                <td className="p-3">{profile.starts_when}</td>
                <td className="p-3">{profile.override_shared_users}</td>
                <td className="p-3 flex justify-center gap-3">
                  <button
                    onClick={() => handleEdit(profile)}
                    className="flex items-center gap-1 px-3 py-2 text-blue-600 hover:text-blue-800 rounded-md border border-blue-600 hover:border-blue-800 text-xs"
                  >
                    <PencilIcon size={16} /> Edit
                  </button>
                  <button
                    onClick={() => handleDelete(profile.name)}
                    className="flex items-center gap-1 px-3 py-2 text-red-600 hover:text-red-800 rounded-md border border-red-600 hover:border-red-800 text-xs"
                  >
                    <TrashIcon size={16} /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex justify-center gap-2">
        {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
          <button
            key={page}
            onClick={() => setCurrentPage(page)}
            className={`px-3 py-1 rounded-lg border ${
              page === currentPage
                ? "bg-blue-600 text-white"
                : "bg-white text-black"
            }`}
          >
            {page}
          </button>
        ))}
      </div>
    </div>
  );
};

export default UsermanProfiles;
